import React, { useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import _snakeCase from 'lodash/snakeCase'
import isNil from 'lodash/isNil'
import { getBoundActions } from '../../actions/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { TYPE_LIVE_ACCESS_EMAIL_CAPTURE } from '../../services/dialog/index'
import Link from '../Link/index'
import {
  H2, H3, H5, H6,
} from '../Heading/index'
import { URL_GAIA_PLUS } from '../../services/url/constants'
import Icon, { ICON_TYPES } from '../Icon.v2/index'
import Button, { TYPES } from '../Button/index'
import { transformEventRoute } from '../../services/live-access-events/index'

const moreInfoRoutes = {
  'marisa-peer-changing-your-life-is-easy': '/lp/marisa-peer',
}

const PAST_EVENT_URL = '/topic/watch-on-demand'
const ONEDAY = 1000 * 60 * 60 * 24

export const isEventCancelled = (startDate) => {
  return (new Date(startDate).getFullYear()) === 2000
}
export const stripYear = (dateString) => {
  const str = dateString.substring(0, dateString.lastIndexOf(' '))
  return str.split(',')[0]
}

const shouldSaleInfoRender = (isEventsPlusMember, onlyText) => {
  if (isEventsPlusMember && !onlyText) {
    return true
  }
  return !isEventsPlusMember && onlyText
}
const shouldRemindMeButtonRender = (onlyText, showSaleInfo, isEventsPlusMember) => {
  if (!onlyText && showSaleInfo && !isEventsPlusMember) {
    return true
  }
  if (!onlyText && !showSaleInfo && !isEventsPlusMember) {
    return true
  }
  return !!(!onlyText && showSaleInfo && isEventsPlusMember)
}

function addModal (
  saleInfo,
  id,
  remindMe,
  renderModal,
  email,
  staticText,
  speakerName,
  isEventsPlusMember,
  onlyText,
) {
  const showSaleInfo = shouldSaleInfoRender(isEventsPlusMember, onlyText)
  const showRemindMeButton = shouldRemindMeButtonRender(onlyText, showSaleInfo, isEventsPlusMember)

  return (
    <div className="saleLine">
      {showSaleInfo && <div>{saleInfo}</div>}

      {(showRemindMeButton)
        ? <div className="RemindMe">
          <span
            className="centerItems"
            onClick={() => {
              renderModal(TYPE_LIVE_ACCESS_EMAIL_CAPTURE, {
                title: staticText.getIn(['data', 'ticketReminder']),
                email,
                id,
                speakerName,
              })
            }}
          >
            <svg className="RemindMeIcon" />
            <span className="remind-me-text">{remindMe}</span>
          </span>
        </div>
        : ''}
    </div>
  )
}

function ticketsOnSale (
  onSaleDate,
  language,
  ticketsAvailable,
  staticText,
  renderModal,
  id,
  email,
  onDemand,
  cancelled,
  speakerName = '',
  isEventsPlusMember = true,
  onlyModalText = false,
) {
  const nonBreakingSpace = '\u00a0'

  if (onDemand) {
    return (
      <span className="centerItems"><svg className="onDemandIcon" />
        {staticText.getIn(['data', 'onDemand']) || nonBreakingSpace}
      </span>
    )
  }
  if (cancelled) {
    return (<div className="SOLD-OUT">{staticText.getIn(['data', 'cancelled']) || nonBreakingSpace}</div>)
  }

  if (!ticketsAvailable) {
    return (<div className="SOLD-OUT">{staticText.getIn(['data', 'soldOut']) || nonBreakingSpace}</div>)
  }

  const remindMe = staticText.getIn(['data', 'remindMe'])
  let saleInfo = staticText.getIn(['data', 'moreInfoSoon'])
  if (!onSaleDate) {
    let SPEAKER_NAME_EVENT_FORM = speakerName
    if (speakerName.length > 0) {
      SPEAKER_NAME_EVENT_FORM = `${_snakeCase(speakerName).toUpperCase()}_EVENT_REMIND_ME_FORM`
    }
    return addModal(
      saleInfo,
      id,
      remindMe,
      renderModal,
      email,
      staticText,
      SPEAKER_NAME_EVENT_FORM,
      isEventsPlusMember,
      onlyModalText,
    )
  }

  const date = new Date(onSaleDate)
  if (Date.now() > date.getTime()) {
    return (<span className="centerItems">
      <svg className="ticketIcon" />
      {staticText.getIn(['data', 'onSaleNow']) || nonBreakingSpace}
    </span>)
  }

  const preSaleDate = new Date(date.getTime() - (7 * ONEDAY))
  const preSale = `${preSaleDate.toLocaleString(language, { month: 'long' })} ${preSaleDate.getDate()}`
  const sale = `${date.toLocaleString(language, { month: 'long' })} ${date.getDate()}`
  saleInfo = `${staticText.getIn(['data', 'preSale'])}: ${preSale} | ${staticText.getIn(['data', 'onSale'])}: ${sale}`

  return addModal(saleInfo, id, remindMe, renderModal, email, staticText, '', isEventsPlusMember)
}

const renderButton = (route, staticText, seriesUrl, language) => {
  const enabled = !!(route || seriesUrl)
  let moreInfo
  if (route) {
    if (language === 'en' && route in moreInfoRoutes) {
      moreInfo = moreInfoRoutes[route]
    } else {
      moreInfo = route.startsWith('/') ? route : `${URL_GAIA_PLUS}/${route}`
    }
    moreInfo = transformEventRoute(moreInfo)
  }

  const text = (seriesUrl && staticText.getIn(['data', 'watchNow']))
    || (moreInfo && staticText.getIn(['data', 'moreInfo']))
    || staticText.getIn(['data', 'comingSoon'])
  return enabled
    ? <Button
      type={TYPES.PRIMARY}
      url={seriesUrl || moreInfo}
      scrollToTop
    >
      {text}
    </Button>
    : <Button
      disabled
    >
      {text}
    </Button>
}

function EventItem ({
  details, language, staticText, renderModal, email, isActive,
  isEventsPlusMember, fixWidth,
}) {
  const {
    descriptionShort, route, eventThumbnailUrl, ticketSaleDate, ticketsAvailable, id,
    routeEnabled, seriesUrl, localeTitleDate, titleShort, speakerNames, startDate,
  } = details.toJS()
  const cancelled = isEventCancelled(startDate)
  const dateString = cancelled ? stripYear(localeTitleDate) : localeTitleDate
  const nonBreakingSpace = '\u00a0'

  const onlyModalText = !isEventsPlusMember
  return (
    <div className={`event-item ${isActive ? 'active' : ''} ${fixWidth} `}>
      <img
        className="event-item__thumbnail"
        src={eventThumbnailUrl || ''}
        alt="thumbnail"
      />
      <div className="event-item__title-speaker">
        <H3 className="event-item__title">{titleShort || nonBreakingSpace}</H3>
        <H5 className="event-item__speaker">
          {(speakerNames.length === 1 && speakerNames[0])
            ? speakerNames[0]
            : nonBreakingSpace}
        </H5>
      </div>
      <div className="event-item__saleText">
        {ticketsOnSale(
          ticketSaleDate,
          language,
          ticketsAvailable,
          staticText,
          renderModal,
          id,
          email,
          !!seriesUrl,
          cancelled,
          speakerNames[0],
          isEventsPlusMember,
          onlyModalText,
        )}
      </div>
      <div className="event-item__long-description">
        {descriptionShort || nonBreakingSpace}
      </div>
      <H6 className="event-item__dates">{dateString || nonBreakingSpace}</H6>
      <div className="event-item__cta">
        {renderButton(routeEnabled && route, staticText, seriesUrl, language)}
        {!isEventsPlusMember && ticketsOnSale(
          ticketSaleDate,
          language,
          ticketsAvailable,
          staticText,
          renderModal,
          id,
          email,
          !!seriesUrl,
          cancelled,
          speakerNames[0],
          isEventsPlusMember,
          false,
        )}
      </div>
    </div>
  )
}

export const EventItemHOC = compose(
  connectRedux(
    (state) => {
      return {
        auth: state.auth,
        app: state.app,
        user: state.user,
        liveAccessEvents: state.liveAccessEvents,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        renderModal: actions.dialog.renderModal,
      }
    },
  ),
  connectStaticText({ storeKey: 'eventsPage' }),
)(EventItem)

const EventList = ({
  eventsList, language, email, staticText, isEventsPlusMember,
}) => {
  const length = isNil(eventsList) ? 0 : eventsList.size
  if (length === 0) {
    return null
  }
  const [currentEvent, setCurrentEvent] = useState(0)
  const TABLET_SIZE = 944
  const DESKTOP_SIZE = 1444
  const nextSlide = () => {
    setCurrentEvent(currentEvent === length - 1 ? 0 : currentEvent + 1)
  }

  const prevSlide = () => {
    setCurrentEvent(currentEvent === 0 ? length - 1 : currentEvent - 1)
  }
  const isActive = (index) => {
    let isActiveItem = false
    switch (renderDependingOnSize()) {
      case 1:
        isActiveItem = index === currentEvent
        break
      case 2:
        isActiveItem = index === currentEvent || index - currentEvent === 1
        break
      case 3:
        isActiveItem = (index === currentEvent
        || index - currentEvent === 1
        || index - currentEvent === 2)
        break
      default:
        break
    }
    return isActiveItem
  }
  const renderDependingOnSize = () => {
    const { innerWidth: screenSize } = window
    if (screenSize / TABLET_SIZE < 1) {
      return 1
    }
    if (screenSize / TABLET_SIZE > 0 && screenSize / DESKTOP_SIZE < 1) {
      return 2
    }
    return 3
  }

  const renderRightControl = () => {
    if ((renderDependingOnSize() === 1 && currentEvent === length - 1)
    || (renderDependingOnSize() === 2 && currentEvent === length - 2)
    || (renderDependingOnSize() === 3 && currentEvent === length - 3)
    || (renderDependingOnSize() > length)) {
      return null
    }

    return (
      <Button
        type={TYPES.LINK}
        className="row-v2__control row-v2__control--next"
        onClick={nextSlide}
      >
        <Icon type={ICON_TYPES.CHEVRON_RIGHT} />
      </Button>
    )
  }
  const fixEventItemWidth = () => {
    if (length === 2 && renderDependingOnSize() === 3) {
      return 'width50'
    } if ((length === 1 && renderDependingOnSize() === 3)
      || (length === 1 && renderDependingOnSize() === 2)) {
      return 'width100'
    }
    return null
  }
  const renderLeftControl = () => {
    return (
      <Button
        type={TYPES.LINK}
        className="row-v2__control row-v2__control--prev"
        onClick={prevSlide}
      >
        <Icon type={ICON_TYPES.CHEVRON_LEFT} />
      </Button>
    )
  }
  return (
    <div className="event-list" >
      <div className="event-list-header">
        <H2 className="event-list__upcoming">{staticText.getIn(['data', 'upcomingEvents'])}</H2>
        <div className="event-list__past">
          <H6>
            <Link
              text={staticText.getIn(['data', 'pastEvents'])}
              to={PAST_EVENT_URL}
            />
          </H6>
        </div>
      </div>
      <hr />
      <div className="event-items">
        {!isEventsPlusMember && currentEvent !== 0 && renderLeftControl(prevSlide)}

        {eventsList && eventsList.map((eventDetail, index) => {
          return (
            (<EventItemHOC
              key={eventDetail.get('id')}
              details={eventDetail}
              language={language}
              isExpanded
              email={email}
              isActive={isActive(index, currentEvent, eventsList)}
              isEventsPlusMember={isEventsPlusMember}
              fixWidth={fixEventItemWidth()}
            />)
          )
        })}

        {!isEventsPlusMember && renderRightControl(nextSlide)}
      </div>
    </div>
  )
}

export default EventList
